import { useEffect, useRef } from "react";

import { FormQuestionConfiguration } from "@whoosh/design-system/src/components/controls";

import { EventFormResponseInput, EventSignUpRegistration } from "../../types";

type HandleChangeProps = {
  id: string;
  value?: string;
  booleanValue?: boolean;
  transportationTypeId?: string;
};

export const useInitalizeFormResponses = ({
  registration,
  eventFormQuestionConfigurations,
  handleChange,
}: {
  registration: EventSignUpRegistration;
  handleChange: ({
    id,
    value,
    booleanValue,
    transportationTypeId,
  }: HandleChangeProps) => void;
  eventFormQuestionConfigurations: FormQuestionConfiguration[];
}) => {
  // Use a ref to store the latest handleChange function
  const handleChangeRef = useRef(handleChange);
  handleChangeRef.current = handleChange;

  useEffect(() => {
    // Only run initialization if we have a registration with registrant data
    if (!registration?.registrant) return;

    eventFormQuestionConfigurations.forEach((questionConfiguration) => {
      const formResponse = getFormResponse(registration, questionConfiguration);

      // Check if we already have a form response for this question
      const existingFormResponse = registration.formResponses?.find(
        (fr) => fr.questionConfigurationId === questionConfiguration.id
      );

      // Only prefill if we have a prefilled value and no existing response
      if (formResponse && !existingFormResponse) {
        handleChangeRef.current({
          id: questionConfiguration.id ?? "",
          value: formResponse.stringValue ?? undefined,
          booleanValue: formResponse.booleanValue ?? undefined,
          transportationTypeId: formResponse.transportationTypeId ?? undefined,
        });
      }
    });
  }, [
    registration?.registrant?.playerId,
    registration?.formResponses,
    eventFormQuestionConfigurations,
    registration,
  ]); // More specific dependencies

  return null;
};

const getFormResponse = (
  registration: EventSignUpRegistration,
  questionConfiguration: FormQuestionConfiguration
): EventFormResponseInput | undefined => {
  const formResponse = registration.formResponses?.find(
    (fr) => fr.questionConfigurationId === questionConfiguration.id
  );

  if (formResponse) {
    return formResponse;
  }

  if (!questionConfiguration.id) {
    return undefined;
  }

  if (questionConfiguration.type === "NAME") {
    return registration.registrant.firstName && registration.registrant.lastName
      ? {
          stringValue: `${registration.registrant.firstName} ${registration.registrant.lastName}`,
          questionConfigurationId: questionConfiguration.id,
        }
      : undefined;
  }
  if (questionConfiguration.type === "GHIN_NUMBER") {
    return registration.registrant.ghinNumber
      ? {
          stringValue: `${registration.registrant.ghinNumber}`,
          questionConfigurationId: questionConfiguration.id,
        }
      : undefined;
  }
  if (questionConfiguration.type === "PHONE_NUMBER") {
    return registration.registrant.phoneNumber
      ? {
          stringValue: registration.registrant.phoneNumber,
          questionConfigurationId: questionConfiguration.id,
        }
      : undefined;
  }
  if (questionConfiguration.type === "EMAIL") {
    return registration.registrant.email
      ? {
          stringValue: registration.registrant.email,
          questionConfigurationId: questionConfiguration.id,
        }
      : undefined;
  }
};
